import {Component, Input, NgZone, OnDestroy, OnInit} from "@angular/core";
import {UserService} from "../../../../user/user.service";
import {DbResponse} from "../../../../shared/interceptors/db-response";
import {Notification} from "../../../../shared/components/notifier/notification";
import {NotifierComponent} from "../../../../shared/components/notifier/notifier.component";
import {NotificationType} from "../../../../shared/components/notifier/notification-type";
import {FormControl} from "@angular/forms";
import {DialogComponent} from "../../../../../ng2-bootstrap-modal/dialog.component";
import {DialogService} from "../../../../../ng2-bootstrap-modal/dialog.service";
import {Subscription} from "rxjs";
import {Product, ProductType} from "../../product";
import {Permissions} from "../../../../user/permissions";
import {ProductService} from '../../product.service';
import {SearchByPropertyPipe} from '../../../../shared/pipes/search-by-property.pipe';
import {Website} from '../../website';
import {UserSubscription} from "../../../../user/user-subscription";

import {DbStoreState} from "../../../../store/reducers";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../store';
import {ProductActionService} from "../../product-action.service";
import {ActivityAction, ActivityStatus, IActivity} from "../../../../activities/activity";
import {User} from "../../../../user/user";
import {filter, first} from "rxjs/operators";

export interface IAvailableThemesData {
  website: Website;
  userPermissions: Permissions;
  userSubscriptions: UserSubscription[];
  currentSubscription: UserSubscription;
}

@Component({
    selector: 'db-available-themes',
    templateUrl: 'available-themes.component.html',
    styleUrls: ['available-themes.component.scss'],
    standalone: false
})
export class AvailableThemesComponent extends DialogComponent<null, boolean> implements OnInit, OnDestroy, IAvailableThemesData {

  public ActivityStatus: any = ActivityStatus;

  @Input() website: Website;
  @Input() userPermissions: Permissions;
  @Input() userSubscriptions: UserSubscription[];
  @Input() currentSubscription: UserSubscription;
  @Input() user: User;

  public loading: boolean;

  private _subscriptions: Subscription[];

  private _productsRequestSubscription: Subscription;

  public title: string;
  public changes: any;

  public selectAll: FormControl;
  public searchTerm: FormControl;

  public activeTab: 'upload'|'wordpress';
  public themes: Product[];
  public filteredThemes: any[];

  public uploadActivity: IActivity;

  constructor(
    private _ngZone: NgZone,
    private _store: Store<DbStoreState>,
    private _searchByPropertyPipe: SearchByPropertyPipe,
    private _userService: UserService,
    private _dialogService: DialogService,
    private _productActionService: ProductActionService,
    private _productService: ProductService
  ) {
    super(_dialogService);
  }

  ngOnInit() {
    this.initBulkActions();
    this.initFilters();

    this.setActiveTab('upload');
    this.addEventHandlers();
  }

  private initBulkActions(): void {
    this.selectAll = new FormControl('');
  }

  private initFilters(): void {
    this.searchTerm = new FormControl('');
  }

  public setActiveTab(tabId: 'upload'|'wordpress'): void {
    this.activeTab = tabId;
    this.loading = true;
    setTimeout((): void => {
      this.updateContent();
    }, 150);
  }

  private updateContent(): void {
    this.themes = null;
    this.filteredThemes = null;
    switch (this.activeTab) {

      case 'upload':
        this.loading = false;
        break;

      case 'wordpress':
        this.getWordpressThemes();
        break;
    }
  }

  private getWordpressThemes() {
    if (this._productsRequestSubscription) {
      this._productsRequestSubscription.unsubscribe();
      this._productsRequestSubscription = null;
    }

    this._productsRequestSubscription = this._productService.getWordpressThemes(this.website?.id, this.searchTerm?.value)
      .pipe(
        filter((response: DbResponse): boolean => !!response),
        first())
      .subscribe((response: DbResponse): void => {
        if (response.success) {
          this.themes = response.data;
          this.filteredThemes = this.themes.map((theme: Product): any => {
            return theme;
          });
        }
        else {
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        }
        this.loading = false;
      });
  }

  private addEventHandlers(): void {

    this._subscriptions = [];

    this._subscriptions.push(this.searchTerm.valueChanges.subscribe((): void => {
      switch (this.activeTab) {
        case 'wordpress':
          this.getWordpressThemes();
          break;
      }
    }));

    this._subscriptions.push(
      this._store.select(fromStore.getProductUploadOngoingActivity(this.website?.id))
        .subscribe((activity: IActivity) => {
          if (this.uploadActivity && !activity) {
            if (this.uploadActivity.status !== ActivityStatus.FAILED) {
              setTimeout(() => {
                this.uploadActivity = activity;
              }, 2000);
            }
            if (this.uploadActivity.status === ActivityStatus.DONE) {
              setTimeout(() => {
                this.close();
              }, 2000);
            }
          }
          else  {
            this.uploadActivity = activity;
          }
        })
    );
  }

  override ngOnDestroy(): void {
    this.removeEventHandlers();
  }

  private removeEventHandlers(): void {

    if (this._productsRequestSubscription) {
      this._productsRequestSubscription.unsubscribe();
      this._productsRequestSubscription = null;
    }

    if ( this._subscriptions ) {
      this._subscriptions.forEach((subscription: Subscription): void => {
        subscription.unsubscribe();
      });
    }
    this._subscriptions = null;
  }

  public onThemeArchiveSelect($event: any): void {
    this.uploadtheme($event.target.files[0]);
  }

  public onThemeArchiveDrop($event: any): void {
    const uploadFile: any = $event.files[0];
    if (!uploadFile || !uploadFile.fileEntry) {
      return;
    }

    uploadFile.fileEntry.file((file: File): void => {
      this._ngZone.run(() => {
        this.uploadtheme(file);
      });
    });
  }

  private uploadtheme(file: File): void {
    this._store.dispatch(new fromStore.InitWebsiteProductUploadAction({
      website: this.website,
      products: [Product.createUploadProduct(file, ProductType.THEME)],
      action: ActivityAction.INSTALL
    }));
  }

  public resetUploadState(): void {
    this.uploadActivity = null;
  }

}
